import { formatDate } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  forwardRef,
  Injector,
  Input,
  OnInit
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ACLService } from '@delon/acl';

import { EInvoiceService } from '../../../core/services/e-invoice.service';
import { AutoCodeService as AutoCodeService } from '../../services/auto-code.service';

@Component({
  selector: 'app-auto-code',
  templateUrl: './auto-code.component.html',
  styleUrls: ['./auto-code.component.less'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AutoCodeComponent),
      multi: true
    }
  ],
  standalone: false
})
export class AutoCodeComponent implements ControlValueAccessor, OnInit {
  disabled = false;
  //@Input() value: string = '';
  @Input() maxLength: string | number | null = null;
  @Input() readonly: boolean = false;
  @Input() type!:
    | 'AR'
    | 'AP'
    | 'AM'
    | 'BIR'
    | 'JV'
    | 'PV'
    | 'OR'
    | 'INV'
    | 'DN'
    | 'CN'
    | 'RF'
    | 'BIL'
    | 'CDN'
    | 'CCN'
    | 'CRF'
    | 'QT'
    | 'SO'
    | 'DO'
    | 'CS'
    | 'CSR'
    | 'CP'
    | 'CPF'
    | 'PR'
    | 'PO'
    | 'GRN'
    | 'SADJ'
    | 'STT'
    | 'SOB'
    | 'EINV'
    | 'SIN'
    | 'SOUT'
    | 'DP'
    | 'DPR'
    | 'SP';

  private _date: Date | null | undefined = new Date();
  @Input() get date(): Date | null | undefined {
    return this._date;
  }
  set date(val: Date | null | undefined) {
    this._date = val;
    if (val) this.onParamsChanged();
  }

  private _projectId: number | undefined | null = null;
  @Input() get projectId(): number | undefined | null {
    return this._projectId;
  }
  set projectId(val: number | undefined | null) {
    this._projectId = val;
    this.onParamsChanged();
  }

  private _controlAccount: string | undefined | null = null;
  @Input() get controlAccount(): string | undefined | null {
    return this._controlAccount;
  }
  set controlAccount(val: string | undefined | null) {
    this._controlAccount = val;
    if (val) this.onParamsChanged();
  }

  private _companyName: string = '';
  @Input() get companyName(): string {
    return this._companyName;
  }
  set companyName(val: any) {
    this._companyName = val;
    if (val) this.onParamsChanged();
  }

  private _isNew = false;
  @Input() get isNew() {
    return this._isNew;
  }
  set isNew(val: boolean) {
    this._isNew = val;
    if (val) {
      this.onParamsChanged();
    }
  }

  private _isDraftApplicable: boolean = false;
  @Input() get isDraftApplicable(): boolean {
    return this._isDraftApplicable;
  }
  set isDraftApplicable(val: boolean) {
    this._isDraftApplicable = val;
    if (val) this.onParamsChanged();
  }

  get manualCode() {
    return this.autoCodeSrv.value &&
      this.autoCodeSrv.value.indexOf('auto-') === -1
      ? this.autoCodeSrv.value
      : '';
  }

  get placeHolder() {
    const format = this.autoCodeSrv.activeFormat;
    if (format) {
      if (format.sampleCode) return format.sampleCode;
      else return format.format;
    }
    return ''; // { Sequence Code }
  }

  get isReadonly() {
    return (this.disabled || this.readonly);
  }
  onTouched: any = () => {};

  constructor(
    protected injector: Injector,
    private cdr: ChangeDetectorRef,
    public eInvoiceSrv: EInvoiceService,
    public autoCodeSrv: AutoCodeService
  ) {}

  ngOnInit(): void {
    this.autoCodeSrv.reset();

    this.autoCodeSrv.codeLoaded = (format: any) => {
      this.cdr.detectChanges();
    };
  }

  get acl() {
    return this.injector.get(ACLService);
  }

  writeValue(value: string): void {
    this.autoCodeSrv.value = value;
  }

  registerOnChange(fn: any): void {
    this.autoCodeSrv.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  private getQueryParams() {
    const params: any = {
      type: this.type,
      isDraftApplicable: this.isDraftApplicable
    };
    if (this.type === 'AR' || this.type === 'AP') {
      if (this.controlAccount) {
        params.controlAccount = this.controlAccount;
      }
      if (this.companyName) {
        params.companyName = this.companyName;
      }
    } else {
      if (this.date) params.date = formatDate(this.date, 'yyyy-MM-dd', 'en-US');
      if (this.projectId) params.projectId = this.projectId;
      if (this.type === 'PV' || this.type === 'OR') {
        if (this.controlAccount) {
          params.controlAccount = this.controlAccount;
        }
      }
    }
    return params;
  }

  selectFormat(format: any) {
    if (format) {
      this.autoCodeSrv.activeFormat = format;
      this.autoCodeSrv.value = `auto-${format.id}`;
      this.autoCodeSrv.onChange(this.autoCodeSrv.value);
      this.cdr.markForCheck();
    }
  }

  onModelChange(val: string) {
    // reset when users clear the docCode
    if (!val && this.isNew) {
      this.selectFormat(this.autoCodeSrv.activeFormat);
    } else {
      this.autoCodeSrv.value = val;
      this.autoCodeSrv.onChange(val);
      this.cdr.markForCheck();
    }
  }

  onClick(e: any) {
    if (!window.getSelection()?.toString()) {
      e.select();
    }
  }

  // Limit the range of input keys for E-invoice code
  // Only allows numbers, letters, '-', '/'
  onKeydown(event: any) {
    if (event.ctrlKey || event.metaKey) return;

    const key = `${event.key}`;
    if (key.length !== 1) return;

    const allow =
      (key >= '0' && key <= '9') ||
      (key >= 'A' && key <= 'Z') ||
      (key >= 'a' && key <= 'z') ||
      key === '-' ||
      key === '/';
    if (!allow) {
      event.preventDefault();
    }
  }

  private onParamsChanged() {
    if (!this.isNew) return;

    if ((this.type === 'AR' || this.type == 'AP') && !this._controlAccount)
      return;

    const params = this.getQueryParams();

    if (!params) return;

    // always load bir format
    if (
      this.type !== 'BIR' &&
      this.autoCodeSrv.loadedParams === JSON.stringify(params)
    )
      return;

    this.autoCodeSrv.changeParams(params);
  }
}
